/**
 * Resource Internal Rates — API boundary (2026-10-14, docs/specs/resource-internal-rates.md).
 *
 * Money crosses as a decimal STRING, never a JSON number (see src/index.ts). Months cross
 * as `YYYY-MM`; the server pins them to the 1st.
 */

import { z } from "zod";

export const internalRateListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
  direction: z.enum(["next", "prev"]).default("next"),
  search: z.string().trim().optional(),
  /** Only Resources with no Internal Rate at all. */
  withoutRate: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => v === "true"),
});

export const addInternalRateSchema = z.object({
  resourceId: z.string().min(1),
  internalCost: z
    .string()
    .trim()
    .regex(/^\d{1,8}(\.\d{1,2})?$/, "Enter an hourly rate like 95 or 95.50."),
  effectiveMonth: z
    .string()
    .trim()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Pick the month the rate starts."),
  note: z.string().trim().max(500).optional(),
});
export type AddInternalRateInput = z.input<typeof addInternalRateSchema>;

export const removeInternalRateSchema = z.object({
  reason: z.string().trim().min(1, "Say why this rate is being removed.").max(500),
});

export const passwordStepUpSchema = z.object({
  password: z.string().min(1, "Enter your password."),
});

// ── Views ─────────────────────────────────────────────────────────────────────
// Zod, not bare interfaces: the client parses every response with these rather than
// casting it (code-quality.md — no `as` at a data boundary).

export const internalRateViewSchema = z.object({
  id: z.string(),
  /** `numeric(10,2)` string, USD per hour. */
  internalCost: z.string(),
  /** `YYYY-MM`. */
  effectiveMonth: z.string(),
});
export type InternalRateView = z.infer<typeof internalRateViewSchema>;

export const internalRateHistoryEntrySchema = internalRateViewSchema.extend({
  note: z.string().nullable(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  /** Set when the rate was removed; a removed rate prices nothing but stays on record. */
  removedAt: z.string().nullable(),
  removalReason: z.string().nullable(),
  removedByName: z.string().nullable(),
});
export type InternalRateHistoryEntryView = z.infer<typeof internalRateHistoryEntrySchema>;
export const internalRateHistorySchema = z.array(internalRateHistoryEntrySchema);

export const resourceDisciplineRatingSchema = z.object({
  code: z.string(),
  name: z.string(),
  rating: z.number().nullable(),
  confirmed: z.boolean(),
});
export type ResourceDisciplineRatingView = z.infer<typeof resourceDisciplineRatingSchema>;

export const suggestedRateSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("suggested"),
    disciplineCode: z.string(),
    disciplineName: z.string(),
    rating: z.number(),
    laborCategoryName: z.string(),
    internalCost: z.string(),
  }),
  z.object({
    kind: z.literal("none"),
    reason: z.enum(["no_confirmed_rating", "no_default_category"]),
    disciplineCode: z.string().optional(),
  }),
]);
export type SuggestedRateView = z.infer<typeof suggestedRateSchema>;

export const resourceInternalRateRowSchema = z.object({
  resourceId: z.string(),
  resourceName: z.string(),
  email: z.string(),
  disciplines: z.array(resourceDisciplineRatingSchema),
  suggestion: suggestedRateSchema,
  /** The rate in effect this month, or null. */
  currentRate: internalRateViewSchema.nullable(),
  /** The next rate scheduled after this month, or null. */
  nextRate: internalRateViewSchema.nullable(),
  rateCount: z.number(),
  /** Live plans (not Closed, Lost or Superseded) naming this Resource on a line. */
  livePlanCount: z.number(),
});
export type ResourceInternalRateRowView = z.infer<typeof resourceInternalRateRowSchema>;

export const resourceInternalRatePageSchema = z.object({
  data: z.array(resourceInternalRateRowSchema),
  nextCursor: z.string().nullable(),
  prevCursor: z.string().nullable(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
  total: z.number(),
});
export type ResourceInternalRatePage = z.infer<typeof resourceInternalRatePageSchema>;

export const stepUpOptionsSchema = z.object({
  password: z.boolean(),
  providers: z.array(z.enum(["google", "microsoft"])),
});
export type StepUpOptionsView = z.infer<typeof stepUpOptionsSchema>;

export const stepUpGrantSchema = z.object({ stepUpId: z.string(), expiresAt: z.string() });
export type StepUpGrantView = z.infer<typeof stepUpGrantSchema>;

/** The error envelope every route returns; `code` marks a step-up refusal. */
export const apiErrorSchema = z.object({
  error: z.string().optional(),
  code: z.string().optional(),
});
